import type {ReactElement} from 'react';
import {useContext, useState} from 'react';
import {DropdownDefaultSelectBox} from '@Components/dropdown-v2/components/dropdown-default-select-box';
import {ALIGNMENT_TYPE, DROPDOWN_SIZE} from '@Components/dropdown-v2/dropdown.types';
import {ControlledList} from '@Components/controlled-list';
import type {ControlledListItem7} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {EventDropdown} from '@Components/event-wizard/event-dropdown';
import {EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT} from '@Components/event-wizard/event-wizard-container-queries-context';
import {QuestionInputType, QUESTION_TYPE_DESCRIPTORS} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {triggerPanelClose} from '@Components/panel/panel-reducer';
import {useAppDispatch} from '@/hooks';
import type {TicketingQuestionTypeDropdownProps} from './ticketing-question-type-dropdown.types';
import {TICKETING_QUESTION_TYPE_LIST_ID, TICKETING_QUESTION_TYPE_PANEL_ID} from './ticketing-question-type-dropdown.types';
import styles from './ticketing-question-type-dropdown.module.scss';

export function TicketingQuestionTypeDropdown({value, onChange}: TicketingQuestionTypeDropdownProps): ReactElement {
  const dispatch = useAppDispatch();
  const {isSmallScreen} = useContext(EVENT_WIZARD_CONTAINER_QUERIES_CONTEXT);

  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

  const typeLabel = window.i18next.t('pmwjs_event_ticketing_type');
  const selectedDescriptor = QUESTION_TYPE_DESCRIPTORS[value];

  const onSelectType = (type: QuestionInputType): void => {
    onChange(type);
    setIsDropdownOpen(false);

    if (isSmallScreen) {
      dispatch(triggerPanelClose(TICKETING_QUESTION_TYPE_PANEL_ID));
    }
  };

  const getListItems = (): ControlledListItem7[] => {
    return Object.values(QuestionInputType).map((type) => {
      const descriptor = QUESTION_TYPE_DESCRIPTORS[type];
      return {
        id: type,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_7,
        icon: descriptor.icon,
        text: window.i18next.t(descriptor.labelKey),
        isSelected: type === value,
        className: type === value ? styles.selectedTypeItem : undefined,
        iconClasses: type === value ? styles.selectedTypeIcon : undefined,
        onClick: (): void => {
          onSelectType(type);
        },
      };
    });
  };

  const getSelector = (): ReactElement => {
    return (
      <DropdownDefaultSelectBox
        className="spacing-m-t-1 _full-width corner-radius-8"
        size={DROPDOWN_SIZE.SMALL}
        icon={selectedDescriptor.icon}
        text={window.i18next.t(selectedDescriptor.labelKey)}
      />
    );
  };

  return (
    <EventDropdown
      text={typeLabel}
      className="_full-width"
      selector={getSelector()}
      popup={<ControlledList className="spacing-m-0" id={TICKETING_QUESTION_TYPE_LIST_ID} items={getListItems()} />}
      popupClass={`${styles.popup} _full-width`}
      alignment={ALIGNMENT_TYPE.BOTTOM_START}
      open={isDropdownOpen}
      panelIdForModal={TICKETING_QUESTION_TYPE_PANEL_ID}
      showModalForSmallScreen={isSmallScreen}
      modalTitle={typeLabel}
      doCloseOnInnerClick
      onOpen={(): void => {
        setIsDropdownOpen(true);
      }}
      onClose={(): void => {
        setIsDropdownOpen(false);
      }}
    />
  );
}
